'use client'

import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { User } from '@/types'
import { UserInfoContext } from './UserInfoContext'

interface UserInfoProviderProps {
  children: ReactNode
  initialUserInfo: Partial<User> | null
}

export const UserInfoProvider = ({
  children,
  initialUserInfo,
}: UserInfoProviderProps) => {
  const [userInfo, setUserInfo] = useState<Partial<User> | null>(
    initialUserInfo
  )

  // Stable identity matters: consumers (e.g. AuthorizationContainer) list
  // setUserInfo in effect dependency arrays. An inline function here would be
  // a new reference on every provider render, so any effect that both calls
  // setUserInfo and depends on it re-fires itself forever — in practice a
  // Server Action POST loop against the current page URL.
  const updateUserInfo = useCallback((newUserInfo: Partial<User> | null) => {
    setUserInfo(newUserInfo)
  }, [])

  const contextValue = useMemo(
    () => ({ userInfo, setUserInfo: updateUserInfo }),
    [userInfo, updateUserInfo]
  )

  return (
    <UserInfoContext.Provider value={contextValue}>
      {children}
    </UserInfoContext.Provider>
  )
}
